import { useState, useMemo } from 'react';
import Switch from 'components/shared/Switch';
import {  FiEdit2, FiChevronUp, FiChevronDown, FiMoreVertical, FiPlus } from 'react-icons/fi';
import { Permission, permissions } from '@/lib/constants';
import SearchBar from 'components/shared/SearchBar';
import FeatureModal from './FeatureModal';
import { Tooltip } from 'react-tooltip';

export default function FeaturesContent() {
    const [selectedPlan, setSelectedPlan] = useState('Trial Plan');
    const [openPlansDropdown, setOpenPlansDropdown] = useState(false);
    const [permissionsData, setPermissionsData] = useState(permissions);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const plans = [
        { id: 'select', label: 'Select plan', disabled: true },
        { id: 'trial_plan', label: 'Trial Plan' },
        { id: 'basic_plan', label: 'Basic Plan' },
        { id: 'pro_plan', label: 'Pro Plan' },
        { id: 'enterprise_plan', label: 'Enterprise Plan' }
    ];

    // Add search filter functionality
    const filteredPermissions = useMemo(() => {
        if (!searchTerm) return permissionsData;
        
        return permissionsData.filter(module => 
            module.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            module.functionalities.some(func => 
                func.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [permissionsData, searchTerm]);

    const handleModuleToggle = (moduleId: string) => {
        setPermissionsData(prevData => {
            return prevData.map(module => {
                if (module.id === moduleId) {
                    const newEnabled = !module.isEnabled;
                    return {
                        ...module,
                        isEnabled: newEnabled,
                        functionalities: module.functionalities.map(func => ({
                            ...func,
                            isEnabled: newEnabled // Toggle all functionalities based on module state
                        }))
                    };
                }
                return module;
            });
        });
    };
    
    const handleFunctionalityToggle = (moduleId: string, functionalityId: string) => {
        setPermissionsData(prevData => {
            return prevData.map(module => {
                if (module.id === moduleId) {
                    const newFunctionalities = module.functionalities.map(func => {
                        if (func.id === functionalityId) {
                            return { ...func, isEnabled: !func.isEnabled };
                        }
                        return func;
                    });
    
                    // Ensure module's isEnabled is true if any functionality is enabled, and false if none are
                    const anyFunctionalitiesEnabled = newFunctionalities.some(f => f.isEnabled);
    
                    return {
                        ...module,
                        isEnabled: anyFunctionalitiesEnabled, // Only disable module if no functionalities are enabled
                        functionalities: newFunctionalities
                    };
                }
                return module;
            });
        });
    };
    


    const handleEdit = (module: Permission) => {
        setEditingPermission(module);
        setShowModal(true);
    };

    // Add this helper function at the top
    const isTruncated = (text: string, limit: number): boolean => {
        return text.length > limit;
    };

    return (
        <div className="space-y-6">
            <div className="sticky top-[100px] bg-black z-10 pt-4">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <button
                                onClick={() => setOpenPlansDropdown(!openPlansDropdown)}
                                className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
                                style={{
                                    minWidth: '140px',
                                    maxWidth: '140px',
                                }}
                            >
                                <span className="truncate mr-2">{selectedPlan}</span>
                                {openPlansDropdown ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
                            </button>

                            {openPlansDropdown && (
                                <>
                                    <div
                                        className="fixed inset-0 z-10"
                                        onClick={() => setOpenPlansDropdown(false)}
                                    />
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
                                        {plans.map((plan) => (
                                            <button
                                                key={plan.id}
                                                onClick={() => {    
                                                    if (!plan.disabled) {
                                                        setSelectedPlan(plan.label);
                                                        setOpenPlansDropdown(false);
                                                    }
                                                }}
                                                disabled={plan.disabled}
                                                className={`w-full text-left px-4 py-2 ${
                                                    plan.disabled 
                                                        ? 'text-gray-500 bg-[#2A2A2A] cursor-default opacity-50' 
                                                        : 'text-gray-400 hover:bg-[#3a3a3a33] hover:text-white'
                                                } truncate`}
                                            >
                                                {plan.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <SearchBar
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder="Search..."
                        />
                    </div>

                    <button
                        onClick={() => {
                            setShowModal(true);
                            setEditingPermission(null);
                        }}
                        className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white hover:bg-[#3a3a3a33]"
                    >
                        <FiPlus /> Add Feature
                    </button>
                </div>

                <div className="bg-[#2A2A2A] rounded-t-lg overflow-hidden">
                    <table className="w-full border-collapse">
                        <thead className="bg-[#1A1A1A] text-gray-400 text-sm">
                            <tr className="bg-[#1A1A1A] text-gray-400 text-sm">
                                <th className="text-left p-4 w-[300px]">Feature</th>
                                <th className="text-left p-4 w-[200px]">Item</th>
                                <th className="text-left p-4">Description</th>
                                <th className="text-center p-4 w-[100px]">Actions</th>
                            </tr>
                        </thead>
                    </table>
                </div>
            </div>

            <div className="bg-[#2A2A2A]" style={{margin: 0}}>
                <table className="w-full border-collapse">
                    <tbody>
                        {filteredPermissions.map((module) => (
                            <tr key={module.id} className="border-b border-white/[0.1] hover:bg-white/[0.02]">
                                <td className="border-r border-white/[0.1] h-[64px]">
                                    <div className="h-full px-4 flex items-center justify-between">
                                        <span className="text-white">{module.name}</span>
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={module.isEnabled}
                                                onChange={() => handleModuleToggle(module.id)}
                                            />
                                            <div className="relative">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenMenuId(openMenuId === module.id ? null : module.id);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-white/[0.08]"
                                                >
                                                    <FiMoreVertical size={16} />
                                                </button>
                                                
                                                {openMenuId === module.id && (
                                                    <>
                                                        <div
                                                            className="fixed inset-0 z-10"
                                                            onClick={() => setOpenMenuId(null)}
                                                        />
                                                        <div className="absolute right-0 mt-1 w-32 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
                                                            <button
                                                                onClick={() => {
                                                                    handleEdit(module);
                                                                    setOpenMenuId(null);
                                                                }}
                                                                className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white flex items-center gap-2"
                                                            >
                                                                <FiEdit2 size={14} />
                                                                <span>Edit</span>
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="align-top border-r border-white/[0.1]" colSpan={2}>
                                    {module.functionalities.map((functionality, index): JSX.Element => (
                                        <div 
                                            key={functionality.id} 
                                            className={`flex items-center ${
                                                index !== module.functionalities.length - 1 ? 'border-b border-white/[0.1]' : ''
                                            }`}
                                        >
                                            <div className="w-[200px] px-4 py-4 text-gray-400">
                                                <div 
                                                    className="max-w-full truncate"
                                                    {...(isTruncated(functionality.name, 15) && {
                                                        'data-tooltip-id': 'functionality-name-tooltip',
                                                        'data-tooltip-content': functionality.name
                                                    })}
                                                >
                                                    {functionality.name.length > 15 
                                                        ? functionality.name.substring(0, 15) + '...' 
                                                        : functionality.name
                                                    }
                                                </div>
                                            </div>
                                            <div className="flex-1 px-4 py-4 text-gray-400">
                                                <div 
                                                    className="truncate whitespace-normal overflow-hidden text-sm leading-5"
                                                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                                                    {...(functionality.description.length > 100 && {
                                                        'data-tooltip-id': 'functionality-desc-tooltip',
                                                        'data-tooltip-content': functionality.description
                                                    })}
                                                >
                                                    {functionality.description}
                                                </div>
                                            </div>
                                            <div className="w-[100px] flex justify-center py-4">
                                                <Switch
                                                    checked={functionality.isEnabled}
                                                    onChange={() => handleFunctionalityToggle(module.id, functionality.id)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Tooltips */}
            <Tooltip 
                id="functionality-name-tooltip" 
                place="top"
                className="max-w-md bg-[#1A1A1A] border border-white/[0.1] !opacity-100"
            />
            <Tooltip 
                id="functionality-desc-tooltip" 
                place="bottom"
                className="max-w-md bg-[#1A1A1A] border border-white/[0.1] !opacity-100"
                style={{
                    marginTop: '-10px',
                    transform: 'translate3d(0, 5px, 0)'
                }}
            />

            <FeatureModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditingPermission(null);
                }}
                mode={editingPermission ? 'edit' : 'create'}
                initialData={editingPermission}
            />
        </div>
    );
} 